import { useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDown,
  Check,
  Loader2,
  Sparkles,
  Radio,
  Users,
  Megaphone,
  Layers,
  Clock,
  ArrowLeft,
} from "lucide-react";
import Nav from "../components/Nav";
import { LOGO_URL } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  {
    n: "01",
    icon: Sparkles,
    title: "Premium Visibility",
    body: "Your brand featured across the event, creatives and every piece of media coverage that leaves the room.",
  },
  {
    n: "02",
    icon: Radio,
    title: "Multi-Channel Reach",
    body: "Amplification across social, press and influencer channels — before, during and long after the night.",
  },
  {
    n: "03",
    icon: Users,
    title: "Audience Alignment",
    body: "Connect with an engaged, entertainment-loving audience that shows up ready to remember.",
  },
  {
    n: "04",
    icon: Megaphone,
    title: "Association with Buzz",
    body: "Be part of a moment people talk about, screenshot and share. Cultural currency, not banner ads.",
  },
  {
    n: "05",
    icon: Layers,
    title: "Flexible Packages",
    body: "Sponsorship tiers built for different goals and budgets. We shape the deal around your outcomes.",
  },
  {
    n: "06",
    icon: Clock,
    title: "Early Access Advantage",
    body: "Sponsors get in before public announcements — better placement, better price, first pick.",
  },
];

const heroContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.15 } },
};
const heroLine = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1] } },
};

function SponsorHero({ onCta }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
    >
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 -z-10 will-change-transform"
      >
        <img
          src="https://images.unsplash.com/photo-1506157786151-b8491531f063?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxtdXNpYyUyMGZlc3RpdmFsJTIwY3Jvd2QlMjBsaWdodHN8ZW58MHx8fHwxNzgzNjU5OTYzfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-[100svh] flex flex-col justify-between">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex items-center gap-3 border border-white/15 rounded-full pl-3 pr-4 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#FF007F] animate-pulse" />
            <span className="font-mono-em text-[0.7rem] tracking-[0.28em] uppercase text-white/80">
              Upcoming Event · Sponsorships Open
            </span>
          </motion.div>

          <motion.h1
            variants={heroContainer}
            initial="hidden"
            animate="show"
            className="mt-8 md:mt-12 font-display font-extrabold text-white leading-[0.9] tracking-tighter"
            style={{ fontSize: "clamp(3rem, 8.5vw, 9rem)" }}
          >
            <span className="mask-line">
              <motion.span variants={heroLine} className="block">
                EVENT MONK
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={heroLine} className="block gradient-text">
                Presents.
              </motion.span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
          >
            An exciting new event is on the way — and a limited number of
            sponsorship opportunities are open for brands who want front-row
            visibility with an engaged audience.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <button
              onClick={onCta}
              data-testid="sponsor-hero-cta"
              className="em-btn em-btn-primary"
            >
              Get Sponsorship Details <ArrowUpRight size={16} />
            </button>
            <Link
              to="/"
              data-testid="sponsor-back-home"
              className="em-btn em-btn-ghost border-white/20"
            >
              <ArrowLeft size={16} /> Back to Event Monk
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1.5 }}
          className="mt-16 flex items-end justify-between border-t border-white/10 pt-6"
        >
          <div className="flex items-center gap-3 text-white/50 font-mono-em text-[0.7rem] tracking-[0.24em] uppercase">
            <ArrowDown size={14} className="animate-bounce" /> Why Partner
          </div>
          <div className="font-mono-em text-[0.7rem] tracking-[0.24em] uppercase text-white/50 text-right">
            <div>Sponsorships · 2026</div>
            <div className="mt-1 hidden md:block">Limited Slots · Early Access</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function WhyPartner() {
  return (
    <section id="why-partner" className="relative py-24 md:py-32 border-t border-white/10">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-8 items-end mb-14 md:mb-20"
        >
          <div className="col-span-12 md:col-span-7">
            <span className="em-tag">01 — Why partner</span>
            <h2
              className="mt-6 font-display font-extrabold tracking-tighter leading-[0.98]"
              style={{ fontSize: "clamp(2rem, 4.6vw, 4.2rem)" }}
            >
              Six reasons brands choose us early.
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 text-white/70 leading-relaxed">
            We don't sell logos on backdrops. Each sponsor becomes a chapter in the event narrative — visible, useful, remembered.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.article
                key={b.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="em-card p-6 md:p-8 group"
                data-testid={`benefit-card-${b.n}`}
              >
                <div className="flex items-center justify-between">
                  <div className="h-11 w-11 grid place-items-center border border-white/15 rounded-full group-hover:border-white/40 transition-colors duration-500">
                    <Icon size={18} className="text-white" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono-em text-[0.7rem] tracking-[0.24em] text-white/45">
                    / {b.n}
                  </span>
                </div>
                <h3 className="mt-6 font-display font-bold text-xl md:text-[1.4rem] tracking-tight leading-[1.15]">
                  {b.title}
                </h3>
                <p className="mt-3 text-white/65 text-sm md:text-base leading-relaxed">
                  {b.body}
                </p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SponsorForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) {
      setError("Name, company and email are required.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/sponsor-leads`, form);
      setStatus("success");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Please check your inputs.");
      setStatus("error");
    }
  };

  return (
    <section
      id="signup"
      className="relative py-24 md:py-32 border-t border-white/10"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 md:gap-12">
          <div className="col-span-12 md:col-span-5">
            <span className="em-tag">02 — Interested in Sponsoring?</span>
            <h2
              className="mt-6 font-display font-extrabold tracking-tighter leading-[0.98]"
              style={{ fontSize: "clamp(2rem, 4.6vw, 4rem)" }}
            >
              Get on the shortlist.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              Drop your details and we'll send you the sponsorship deck,
              tier options and everything you need to decide fast.
            </p>

            <div className="mt-10 space-y-4 text-white/75 text-sm">
              <div className="flex items-center gap-3">
                <span className="h-1 w-1 rounded-full bg-white/40" />
                Sponsorship deck within 24 hours
              </div>
              <div className="flex items-center gap-3">
                <span className="h-1 w-1 rounded-full bg-white/40" />
                One direct call with the founding team
              </div>
              <div className="flex items-center gap-3">
                <span className="h-1 w-1 rounded-full bg-white/40" />
                Priority pick before public announcement
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="sponsor-form"
            className="col-span-12 md:col-span-7 md:pl-12 border-t md:border-t-0 md:border-l border-white/10 pt-10 md:pt-0"
          >
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="sponsor-success"
                  className="min-h-[400px] flex flex-col justify-center"
                >
                  <div className="h-14 w-14 rounded-full grid place-items-center border border-white/20">
                    <Check size={22} />
                  </div>
                  <h3
                    className="mt-6 font-display font-extrabold tracking-tighter leading-none"
                    style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
                  >
                    You're on the list.
                  </h3>
                  <p className="mt-4 text-white/70 max-w-md">
                    We've received your interest. Our team will reach out from{" "}
                    <span className="text-white">support@eventmonk.in</span>{" "}
                    within 24 hours with the sponsorship details.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    data-testid="sponsor-send-another"
                    className="em-btn em-btn-ghost border-white/20 mt-8 self-start"
                  >
                    Register another brand
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                    <label className="block">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        01 — Full name *
                      </span>
                      <input
                        data-testid="sponsor-name-input"
                        className="em-input mt-2"
                        placeholder="Your name"
                        value={form.name}
                        onChange={update("name")}
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        02 — Company / Brand *
                      </span>
                      <input
                        data-testid="sponsor-company-input"
                        className="em-input mt-2"
                        placeholder="Brand name"
                        value={form.company}
                        onChange={update("company")}
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        03 — Email *
                      </span>
                      <input
                        data-testid="sponsor-email-input"
                        type="email"
                        className="em-input mt-2"
                        placeholder="you@brand.com"
                        value={form.email}
                        onChange={update("email")}
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                        04 — Phone
                      </span>
                      <input
                        data-testid="sponsor-phone-input"
                        className="em-input mt-2"
                        placeholder="+91 …"
                        value={form.phone}
                        onChange={update("phone")}
                      />
                    </label>
                  </div>

                  <label className="block mt-10">
                    <span className="font-mono-em text-[0.68rem] tracking-[0.24em] uppercase text-white/50">
                      05 — Notes / message
                    </span>
                    <textarea
                      data-testid="sponsor-message-input"
                      className="em-input mt-2 resize-none"
                      rows={3}
                      placeholder="Tell us about your brand and what you're looking to achieve…"
                      value={form.message}
                      onChange={update("message")}
                    />
                  </label>

                  {status === "error" && (
                    <p className="mt-6 text-sm text-[#FF4D66]">{error}</p>
                  )}

                  <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
                    <p className="text-white/50 text-xs max-w-sm">
                      We'll only use this to share sponsorship details. No spam.
                    </p>
                    <button
                      data-testid="sponsor-submit-button"
                      type="submit"
                      disabled={status === "loading"}
                      className="em-btn em-btn-primary disabled:opacity-70"
                    >
                      {status === "loading" ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Notify Me — Send Details <ArrowUpRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </section>
  );
}

function MiniFooter() {
  return (
    <footer className="relative border-t border-white/10 bg-black">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-14 md:py-20 grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5 flex items-center gap-3">
          <img src={LOGO_URL} alt="Event Monk" className="h-10 w-10 object-contain" />
          <div className="leading-none">
            <div className="font-display font-bold text-base tracking-tight">EVENT MONK</div>
            <div className="font-mono-em text-[0.62rem] tracking-[0.24em] uppercase text-white/50 mt-1">
              Events Reimagined
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-4">
          <div className="font-mono-em text-[0.7rem] tracking-[0.24em] uppercase text-white/50">
            Direct
          </div>
          <a href="mailto:support@eventmonk.in" className="mt-3 block em-underline-link">
            support@eventmonk.in
          </a>
          <a href="tel:+919562451105" className="mt-1 block em-underline-link">
            +91 95624 51105
          </a>
        </div>
        <div className="col-span-12 md:col-span-3 md:text-right">
          <Link to="/" className="em-underline-link font-mono-em text-[0.7rem] tracking-[0.24em] uppercase text-white/70">
            eventmonk.in
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-6 flex flex-wrap gap-3 items-center justify-between text-white/50 font-mono-em text-[0.68rem] tracking-[0.22em] uppercase">
          <span>© 2026 Event Monk.</span>
          <span>Sponsorships · Limited Slots</span>
        </div>
      </div>
    </footer>
  );
}

export default function Sponsorships() {
  const scrollToForm = () => {
    const el = document.getElementById("signup");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="sponsorships-page" className="bg-[#050505]">
      <Nav />
      <SponsorHero onCta={scrollToForm} />
      <WhyPartner />
      <SponsorForm />
      <MiniFooter />
    </main>
  );
}
